import { AbstractCustomRuleFormSectionWebComponent } from '../../AbstractCustomRuleFormSectionWebComponent';
import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../../../ui/gzTitle';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../ui/gzDropdown';
import { TemplateState } from '../../../../../types/common/types';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../../../../ui/gzForm/validators/remote.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { getIntegrityMonitorSeverityValues } from '../../../../common/utils/types';
import { getIntegrityMonitorEntityTypes } from '../types';
import { IMConstants } from '../../../../common/utils/IMConstants';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { MinLengthValidator } from '../../../../../ui/gzForm/validators/min-length.validator';

export class IntegrityMonitorCustomRuleMainFormView extends BaseWebComponentView {
  public formGroup: FormGroup;
  public editRuleId: string | null = null;

  public dom: {
    $wrapper: HTMLElement;

    $generalSubtitle: GzTitleWebComponent;

    $ruleNameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $ruleDescriptionFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $ruleSeverityFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $ruleEntityTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

    $entitySpecificForms: Array<AbstractCustomRuleFormSectionWebComponent | null>;
  };

  getDomElements() {
    const entityTypes = getIntegrityMonitorEntityTypes();

    this.dom = {
      $wrapper: this.shadow.querySelector('#rule-form-wrapper'),

      $generalSubtitle: this.shadow.querySelector('#general-subtitle'),

      $ruleNameFormField: this.shadow.querySelector('#rule-name-form-field'),
      $ruleDescriptionFormField: this.shadow.querySelector('#rule-description-form-field'),
      $ruleSeverityFormField: this.shadow.querySelector('#rule-severity-form-field'),
      $ruleEntityTypeFormField: this.shadow.querySelector('#rule-entitytype-form-field'),

      $entitySpecificForms: new Array(entityTypes.length + 1),
    };

    entityTypes
      .filter(form => form.implemented)
      .forEach(entity => {
        this.dom.$entitySpecificForms[entity.value] = this.shadow.querySelector(entity.formTag);
        this.dom.$entitySpecificForms[entity.value]?.injectDependencies(this.dependencies);
      });

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorRules.customRuleForm;
  }

  setTranslatedStrings() {
    this.dom.$generalSubtitle.innerText = this.translations.generalSubtitle;

    this.dom.$ruleNameFormField.label = this.translations.name;
    this.dom.$ruleNameFormField.Input.placeholder = this.translations.namePlaceholder;

    this.dom.$ruleDescriptionFormField.label = this.translations.description;
    this.dom.$ruleDescriptionFormField.Input.placeholder = this.translations.descriptionPlaceholder;

    this.dom.$ruleSeverityFormField.label = this.translations.severity;
    this.dom.$ruleSeverityFormField.Input.placeholder = this.translations.severityPlaceholder;

    this.dom.$ruleEntityTypeFormField.label = this.translations.entityType;
    this.dom.$ruleEntityTypeFormField.Input.placeholder = this.translations.entityType;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  private initDropdowns(data: any): void {
    this.dom.$ruleSeverityFormField.Input.options = getIntegrityMonitorSeverityValues(
      this.dependencies.translateFn
    ).map(severityLevel => {
      return {
        title: severityLevel.label,
        value: severityLevel.value,
      };
    });

    this.dom.$ruleEntityTypeFormField.Input.options = getIntegrityMonitorEntityTypes()
      .filter(entity => entity.implemented)
      .map(entityType => {
        return {
          title: this.dependencies.translateFn(entityType.label),
          value: entityType.value,
        };
      });
  }

  private createFormGroup() {
    this.dom.$ruleNameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinLengthValidator(IMConstants.MIN_LENGTH_CUSTOM_RULE_NAME, this.translations.minLengthName),
      MaxLengthValidator(IMConstants.MAX_LENGTH_CUSTOM_RULE_NAME, this.translations.maxLengthName),
      RegexValidator(IMConstants.REGEX_NO_SPECIAL_CHARACTERS, this.translations.specialCharactersNotPermitted),
      RemoteValidator(
        value => {
          const companyId = this.dependencies.getActiveCompany().companyId;
          return this.dependencies.api.clients.IntegrityMonitorRules.checkNameIsUnique(
            value,
            companyId,
            this.editRuleId
          );
        },
        (response: { success: boolean }) => response.success,
        this.translations.nameAlreadyExists,
        this.translations.communicationError
      ),
    ];
    this.dom.$ruleDescriptionFormField.Validators = [
      MaxLengthValidator(IMConstants.MAX_LENGTH_CUSTOM_RULE_DESCRIPTION, this.translations.maxLengthDescription),
      RegexValidator(IMConstants.REGEX_NO_SPECIAL_CHARACTERS, this.translations.specialCharactersNotPermitted),
    ];
    this.dom.$ruleSeverityFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$ruleEntityTypeFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$ruleNameFormField.initialValue = '';
    this.dom.$ruleDescriptionFormField.initialValue = '';
    this.dom.$ruleSeverityFormField.initialValue = '';
    this.dom.$ruleEntityTypeFormField.initialValue = '';

    this.formGroup = FormGroup.build({
      general: {
        name: this.dom.$ruleNameFormField,
        description: this.dom.$ruleDescriptionFormField,
        severity: this.dom.$ruleSeverityFormField,
        entityType: this.dom.$ruleEntityTypeFormField,
      },
    });
  }

  private startFormWebComponents(): Promise<void[]> {
    const startPromises: Promise<void>[] = [];

    for (const formComponent of this.dom.$entitySpecificForms) {
      if (formComponent) startPromises.push(formComponent.start());
    }

    return Promise.all(startPromises);
  }

  buildSuccessView(data: any) {
    this.createFormGroup();
    this.initDropdowns(data);
    this.startFormWebComponents().catch(console.warn);
    this.removeLoadingState();
  }

  buildErrorView() {
    this.removeLoadingState();
  }
}
